import React, { useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Grid,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import {
  ArrowDownward,
  ArrowUpward,
  AssignmentTurnedIn,
  CardMembership,
  People,
  SupportAgent,
} from '@mui/icons-material';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { format, subDays } from 'date-fns';
import { tokens } from '../../theme';
import { adminTokens, formatCompactDate, panelSx } from './adminTokens';
import landingForest from '../../assets/landing-forest.png';

const { border: BORDER, editorialSerif } = adminTokens;
const PIE_COLORS = ['#a9bf7a', '#d0b47e', '#c98b67', '#8ec8d7'];

const chartTooltipStyle = {
  backgroundColor: '#111915',
  border: `1px solid ${adminTokens.border}`,
  color: tokens.cream,
  borderRadius: 0,
};

// A null delta means "no comparable previous value" (a point-in-time
// snapshot, not a period-over-period flow) -- render an honest em dash rather
// than a fabricated 0%. This is the general guard for delta rendering; every
// KpiCard consumer routes through it instead of formatting deltaPct inline.
// `undefined` (the prop omitted, or the payload not loaded yet) never reaches
// here: KpiCard's own `hasDelta` gate drops the whole block first.
const formatDelta = (deltaPct) => (
  deltaPct === null ? '—' : `${deltaPct > 0 ? '+' : ''}${deltaPct}%`
);

function KpiCard({ icon, label, value, delta, footnote, accent = tokens.accentStrong }) {
  const hasDelta = delta !== undefined;
  const isComparable = hasDelta && delta !== null;
  const up = isComparable && delta >= 0;
  return (
    <Box sx={panelSx({ p: 1.75, height: '100%' })}>
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Box sx={{ width: 36, height: 36, display: 'grid', placeItems: 'center', border: `1px solid ${adminTokens.borderStrong}`, color: accent }}>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: tokens.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</Typography>
          <Typography variant="h5" sx={{ color: tokens.cream, mt: 0.25, fontFamily: editorialSerif }}>{value}</Typography>
          {hasDelta && (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.35 }}>
              {isComparable && (up ? <ArrowUpward sx={{ fontSize: 12, color: tokens.success }} /> : <ArrowDownward sx={{ fontSize: 12, color: tokens.danger }} />)}
              <Typography variant="caption" sx={{ color: isComparable ? (up ? tokens.success : tokens.danger) : tokens.textMuted }}>
                {formatDelta(delta)}{isComparable ? ' vs prev' : ''}
              </Typography>
            </Stack>
          )}
          {footnote && <Typography variant="caption" sx={{ color: tokens.textMuted, display: 'block', mt: 0.35 }}>{footnote}</Typography>}
        </Box>
      </Stack>
    </Box>
  );
}

function mapActivityTrend(rows = []) {
  return rows.map((row) => ({
    label: format(new Date(`${row.dayKey}T12:00:00.000Z`), 'MMM d'),
    activeUsers: row.activeUsers,
    newUsers: row.newUsers,
    premiumConversions: row.premiumConversions,
    waitlistSignups: row.waitlistSignups,
  }));
}

function mapPremiumTrend(rows = []) {
  return rows.map((row) => ({
    label: format(new Date(`${row.dayKey}T12:00:00.000Z`), 'MMM d'),
    activePremium: row.activePremium,
  }));
}

export default function AdminOverviewView({
  stats,
  overviewAnalytics,
  users,
  subscriptions,
  tickets,
  submissions,
  systemHealth,
  onOpenOperations,
  onOpenSystemHealth,
  onApproveSubmission,
  onRejectSubmission,
  updating,
}) {
  const [overviewFilter, setOverviewFilter] = useState('all');
  const [filterAnchor, setFilterAnchor] = useState(null);

  const kpis = overviewAnalytics?.kpis;
  const premiumSubscribers = kpis?.premiumActive?.current ?? subscriptions.filter((s) => {
    const renewal = s.renewalStatus || s.status;
    return s.isPremium && renewal === 'active';
  }).length;
  const unresolvedTickets = tickets.filter((t) => t.status !== 'resolved' && t.status !== 'closed');
  const totalUsers = stats?.totalUsers || users.length;
  const activeUsers = kpis?.activeUsers30d?.current ?? stats?.activeUsers30d ?? 0;
  const waitlistTotal = kpis?.waitlistTotal?.current ?? overviewAnalytics?.waitlist?.total ?? 0;

  const activityTrend = useMemo(
    () => mapActivityTrend(overviewAnalytics?.activityTrend),
    [overviewAnalytics]
  );
  const premiumTrend = useMemo(
    () => mapPremiumTrend(overviewAnalytics?.premiumTrend),
    [overviewAnalytics]
  );

  const subscriptionPie = useMemo(() => {
    const renewalOf = (s) => s.renewalStatus || s.status || 'pending';
    const active = subscriptions.filter((s) => renewalOf(s) === 'active').length;
    const grace = subscriptions.filter((s) => renewalOf(s) === 'grace').length;
    const cancelled = subscriptions.filter((s) => renewalOf(s) === 'cancelled').length;
    const expired = subscriptions.filter((s) => renewalOf(s) === 'expired').length;
    const trial = subscriptions.filter((s) => s.trialStatus === 'active' || s.isTrialActive).length;
    const total = active + grace + cancelled + expired + trial || 1;
    return [
      { name: 'Active', value: active, pct: Math.round((active / total) * 100) },
      { name: 'Grace', value: grace, pct: Math.round((grace / total) * 100) },
      { name: 'Cancelled', value: cancelled, pct: Math.round((cancelled / total) * 100) },
      { name: 'Expired', value: expired, pct: Math.round((expired / total) * 100) },
      { name: 'Trial', value: trial, pct: Math.round((trial / total) * 100) },
    ].filter((d) => d.value > 0);
  }, [subscriptions]);

  const urgentAlerts = useMemo(() => {
    const alerts = [];
    unresolvedTickets.filter((t) => t.priority === 'critical' || t.priority === 'high').slice(0, 2).forEach((t) => {
      alerts.push({ label: t.subject, time: t.createdAt, tone: tokens.danger });
    });
    submissions.slice(0, 1).forEach((s) => {
      alerts.push({ label: `Flagged food: ${s.productName}`, time: s.createdAt, tone: tokens.clay });
    });
    return alerts.slice(0, 4);
  }, [unresolvedTickets, submissions]);

  const filteredCustomers = useMemo(() => {
    if (overviewFilter === 'premium') {
      return users.filter((u) => u.subscription?.isPremium);
    }
    if (overviewFilter === 'support') {
      const ticketUserIds = new Set(unresolvedTickets.map((t) => t.userId));
      return users.filter((u) => ticketUserIds.has(u.id));
    }
    return users;
  }, [overviewFilter, unresolvedTickets, users]);

  const recentCustomers = filteredCustomers.slice(0, 6);
  const dateRange = `${format(subDays(new Date(), 30), 'MMM d')} – ${format(new Date(), 'MMM d, yyyy')}`;

  return (
    <Box>
      <Box
        sx={{
          p: { xs: 2.5, md: 3 },
          mb: 2,
          border: `1px solid ${BORDER}`,
          backgroundImage: `linear-gradient(180deg, rgba(7,16,13,0.35), rgba(7,16,13,0.72)), url(${landingForest})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
          <Box>
            <Typography variant="h2" sx={{ color: tokens.cream, fontFamily: editorialSerif, fontWeight: 500, fontSize: { xs: '1.85rem', md: '2.2rem' }, mb: 0.5 }}>
              Platform Overview
            </Typography>
            <Typography variant="body2" sx={{ color: tokens.textSoft, maxWidth: 560 }}>
              High-signal snapshot across growth, revenue, support pressure, and content review.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Chip label={dateRange} sx={{ borderRadius: 0, border: `1px solid ${BORDER}`, bgcolor: 'rgba(255,255,255,0.03)', color: tokens.textSoft }} />
            <Button
              variant="outlined"
              size="small"
              onClick={(e) => setFilterAnchor(e.currentTarget)}
            >
              Filters{overviewFilter !== 'all' ? `: ${overviewFilter}` : ''}
            </Button>
            <Menu anchorEl={filterAnchor} open={Boolean(filterAnchor)} onClose={() => setFilterAnchor(null)}>
              {[
                { id: 'all', label: 'All customers' },
                { id: 'premium', label: 'Premium only' },
                { id: 'support', label: 'Open support cases' },
              ].map((item) => (
                <MenuItem
                  key={item.id}
                  selected={overviewFilter === item.id}
                  onClick={() => { setOverviewFilter(item.id); setFilterAnchor(null); }}
                >
                  {item.label}
                </MenuItem>
              ))}
            </Menu>
            <Button variant="contained" size="small" onClick={onOpenOperations}>Open workspace</Button>
          </Stack>
        </Stack>
      </Box>

      <Grid container spacing={1.25} sx={{ mb: 1.5 }}>
        <Grid item xs={6} md={4} lg={2}>
          <KpiCard
            icon={<People fontSize="small" />}
            label="Total customers"
            value={totalUsers.toLocaleString()}
            delta={kpis?.totalUsers?.deltaPct}
            footnote={kpis ? `${kpis.newSignups30d.current} signups (30d)` : undefined}
          />
        </Grid>
        <Grid item xs={6} md={4} lg={2}>
          <KpiCard
            icon={<People fontSize="small" />}
            label="Active (30d)"
            value={activeUsers.toLocaleString()}
            delta={kpis?.activeUsers30d?.deltaPct}
            footnote={`${totalUsers ? ((activeUsers / totalUsers) * 100).toFixed(1) : 0}% of total`}
            accent={tokens.accentStrong}
          />
        </Grid>
        <Grid item xs={6} md={4} lg={2}>
          <KpiCard
            icon={<CardMembership fontSize="small" />}
            label="Premium"
            value={premiumSubscribers.toLocaleString()}
            delta={kpis?.premiumActive?.deltaPct}
            footnote={`${totalUsers ? ((premiumSubscribers / totalUsers) * 100).toFixed(1) : 0}% of total`}
          />
        </Grid>
        <Grid item xs={6} md={4} lg={2}>
          <KpiCard
            icon={<SupportAgent fontSize="small" />}
            label="Support tickets"
            value={`${unresolvedTickets.length} open`}
            delta={kpis?.openTickets?.deltaPct}
            footnote={`${Math.min(4, unresolvedTickets.length)} unassigned`}
            accent="#d49063"
          />
        </Grid>
        <Grid item xs={6} md={4} lg={2}>
          <KpiCard
            icon={<AssignmentTurnedIn fontSize="small" />}
            label="Content queue"
            value={`${submissions.length} review`}
            delta={kpis?.pendingSubmissions?.deltaPct}
            footnote="Food / submissions"
            accent="#d0c37d"
          />
        </Grid>
        <Grid item xs={6} md={4} lg={2}>
          <KpiCard
            icon={<People fontSize="small" />}
            label="Waitlist"
            value={waitlistTotal.toLocaleString()}
            delta={kpis?.waitlistTotal?.deltaPct}
            footnote={kpis ? `${kpis.waitlistSignups30d.current} new (30d)` : 'Early access'}
            accent="#8ec8d7"
          />
        </Grid>
      </Grid>

      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid item xs={12} lg={7}>
          <Box sx={panelSx({ p: 2 })}>
            <Typography variant="h6" sx={{ color: tokens.cream, fontFamily: editorialSerif, mb: 0.5 }}>User activity trend</Typography>
            <Typography variant="caption" sx={{ color: tokens.textMuted, display: 'block', mb: 1.5 }}>Last 30 days</Typography>
            <Box sx={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={activityTrend}>
                  <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis dataKey="label" stroke={tokens.textMuted} tickLine={false} axisLine={false} interval={4} tick={{ fontSize: 10 }} />
                  <YAxis stroke={tokens.textMuted} tickLine={false} axisLine={false} tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={chartTooltipStyle} />
                  <Legend />
                  <Line type="monotone" dataKey="activeUsers" stroke={tokens.accentStrong} strokeWidth={2} dot={false} name="Active users" />
                  <Line type="monotone" dataKey="newUsers" stroke={tokens.textMuted} strokeWidth={1.5} dot={false} name="New users" />
                  <Line type="monotone" dataKey="premiumConversions" stroke={tokens.clay} strokeWidth={1.5} dot={false} name="Premium conversions" />
                  <Line type="monotone" dataKey="waitlistSignups" stroke="#8ec8d7" strokeWidth={1.5} dot={false} name="Waitlist signups" />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={5} lg={3}>
          <Box sx={panelSx({ p: 2, height: '100%' })}>
            <Typography variant="h6" sx={{ color: tokens.cream, fontFamily: editorialSerif, mb: 1.5 }}>Subscription status</Typography>
            <Box sx={{ height: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={subscriptionPie} dataKey="value" nameKey="name" innerRadius={48} outerRadius={78} paddingAngle={2}>
                    {subscriptionPie.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={chartTooltipStyle} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
            <Stack spacing={0.5}>
              {subscriptionPie.map((item, i) => (
                <Stack key={item.name} direction="row" justifyContent="space-between">
                  <Typography variant="caption" sx={{ color: tokens.textMuted }}>{item.name}</Typography>
                  <Typography variant="caption" sx={{ color: PIE_COLORS[i] }}>{item.pct}%</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Grid>
        <Grid item xs={12} md={7} lg={2}>
          <Box sx={panelSx({ p: 2, height: '100%' })}>
            <Typography variant="h6" sx={{ color: tokens.cream, fontFamily: editorialSerif, mb: 1.5 }}>Urgent alerts</Typography>
            <Stack spacing={1}>
              {urgentAlerts.map((alert) => (
                <Box key={alert.label} sx={{ p: 1.1, border: `1px solid ${BORDER}`, borderLeft: `3px solid ${alert.tone}`, backgroundColor: 'rgba(255,255,255,0.02)' }}>
                  <Typography variant="body2" sx={{ color: tokens.cream, fontSize: '0.82rem' }} noWrap>{alert.label}</Typography>
                  <Typography variant="caption" sx={{ color: tokens.textMuted }}>{formatCompactDate(alert.time)}</Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        </Grid>
      </Grid>

      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid item xs={12} lg={5}>
          <Box sx={panelSx({ p: 0 })}>
            <Box sx={{ p: 1.75, borderBottom: `1px solid ${BORDER}` }}>
              <Typography variant="h6" sx={{ color: tokens.cream, fontFamily: editorialSerif }}>Recent customers</Typography>
            </Box>
            <Box sx={{ overflowX: 'auto' }}>
              <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <Box component="thead" sx={{ bgcolor: 'rgba(255,255,255,0.02)' }}>
                  <Box component="tr">
                    {['Customer', 'Joined', 'Last active', 'Plan', 'Status'].map((h) => (
                      <Box key={h} component="th" sx={{ textAlign: 'left', p: 1.1, color: tokens.textMuted, fontWeight: 500, borderBottom: `1px solid ${BORDER}` }}>{h}</Box>
                    ))}
                  </Box>
                </Box>
                <Box component="tbody">
                  {recentCustomers.map((user) => (
                    <Box component="tr" key={user.id} sx={{ '&:hover': { bgcolor: 'rgba(169,191,122,0.04)' } }}>
                      <Box component="td" sx={{ p: 1.1, borderBottom: `1px solid ${BORDER}` }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Avatar sx={{ width: 26, height: 26, fontSize: '0.7rem', bgcolor: 'rgba(255,255,255,0.08)' }}>{(user.name || user.email || 'U')[0]}</Avatar>
                          <Box>
                            <Typography variant="caption" sx={{ color: tokens.cream, display: 'block' }}>{user.name || 'Unnamed'}</Typography>
                            <Typography variant="caption" sx={{ color: tokens.textMuted }}>{user.email}</Typography>
                          </Box>
                        </Stack>
                      </Box>
                      <Box component="td" sx={{ p: 1.1, color: tokens.textSoft, borderBottom: `1px solid ${BORDER}` }}>{formatCompactDate(user.createdAt)}</Box>
                      <Box component="td" sx={{ p: 1.1, color: tokens.textSoft, borderBottom: `1px solid ${BORDER}` }}>{formatCompactDate(user.lastUpdated)}</Box>
                      <Box component="td" sx={{ p: 1.1, borderBottom: `1px solid ${BORDER}` }}>
                        <Chip label={user.subscription?.isPremium ? 'Premium' : 'Free'} size="small" sx={{ borderRadius: 0, height: 20, fontSize: '0.68rem' }} />
                      </Box>
                      <Box component="td" sx={{ p: 1.1, borderBottom: `1px solid ${BORDER}` }}>
                        <Chip label={user.isActive === false ? 'Suspended' : 'Active'} size="small" sx={{ borderRadius: 0, height: 20, fontSize: '0.68rem', color: user.isActive === false ? tokens.danger : tokens.success }} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Box sx={panelSx({ p: 2 })}>
            <Typography variant="h6" sx={{ color: tokens.cream, fontFamily: editorialSerif, mb: 0.5 }}>Premium subscribers</Typography>
            <Typography variant="caption" sx={{ color: tokens.textMuted, display: 'block', mb: 1.5 }}>Last 30 days</Typography>
            <Box sx={{ height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={premiumTrend}>
                  <defs>
                    <linearGradient id="premiumFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#a9bf7a" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#a9bf7a" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                  <XAxis dataKey="label" stroke={tokens.textMuted} tickLine={false} axisLine={false} interval={5} tick={{ fontSize: 10 }} />
                  <YAxis stroke={tokens.textMuted} tickLine={false} axisLine={false} tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={chartTooltipStyle} />
                  <Area type="monotone" dataKey="activePremium" stroke={tokens.accentStrong} fill="url(#premiumFill)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
            <Typography variant="h5" sx={{ color: tokens.cream, mt: 1, fontFamily: editorialSerif }}>{Number(premiumSubscribers).toLocaleString()}</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} lg={3}>
          <Box sx={panelSx({ p: 2, mb: 1.5 })}>
            <Typography variant="h6" sx={{ color: tokens.cream, fontFamily: editorialSerif, mb: 1.25 }}>Support queue</Typography>
            <Stack spacing={0.8}>
              {unresolvedTickets.slice(0, 5).map((ticket) => (
                <Box key={ticket.id} sx={{ p: 1, border: `1px solid ${BORDER}`, backgroundColor: 'rgba(255,255,255,0.02)' }}>
                  <Typography variant="caption" sx={{ color: tokens.textMuted }}>#{String(ticket.id).slice(-6)}</Typography>
                  <Typography variant="body2" sx={{ color: tokens.cream, fontSize: '0.82rem' }} noWrap>{ticket.subject}</Typography>
                  <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.35 }}>
                    <Chip label={ticket.priority} size="small" sx={{ borderRadius: 0, height: 18, fontSize: '0.65rem' }} />
                    <Typography variant="caption" sx={{ color: tokens.textMuted }}>{formatCompactDate(ticket.createdAt)}</Typography>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
          <Box sx={panelSx({ p: 2 })}>
            <Typography variant="h6" sx={{ color: tokens.cream, fontFamily: editorialSerif, mb: 1.25 }}>Content review</Typography>
            <Stack spacing={0.8}>
              {submissions.slice(0, 4).map((submission) => (
                <Box key={submission.id || submission._id} sx={{ p: 1, border: `1px solid ${BORDER}` }}>
                  <Typography variant="body2" sx={{ color: tokens.cream, fontSize: '0.82rem' }} noWrap>{submission.productName}</Typography>
                  <Typography variant="caption" sx={{ color: tokens.textMuted }}>{submission.barcode}</Typography>
                  <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                    <Button size="small" variant="contained" disabled={updating} onClick={() => onApproveSubmission?.(submission)} sx={{ minWidth: 0, px: 1, fontSize: '0.68rem' }}>✓</Button>
                    <Button size="small" variant="outlined" color="error" disabled={updating} onClick={() => onRejectSubmission?.(submission)} sx={{ minWidth: 0, px: 1, fontSize: '0.68rem' }}>✕</Button>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
        </Grid>
      </Grid>

      <Box sx={panelSx({ p: 1.5, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' })}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tokens.success, boxShadow: `0 0 8px ${tokens.success}` }} />
          <Typography variant="body2" sx={{ color: tokens.cream }}>System health: {systemHealth?.status === 'operational' ? 'All systems operational' : 'Degraded'}</Typography>
        </Stack>
        {(systemHealth?.services || []).map((service) => (
          <Typography key={service.name} variant="caption" sx={{ color: tokens.textMuted }}>
            {service.name}: {service.status}%
          </Typography>
        ))}
        <Button
          size="small"
          variant="text"
          sx={{ ml: 'auto', color: tokens.accentStrong, fontSize: '0.75rem' }}
          onClick={() => onOpenSystemHealth?.()}
        >
          View system health →
        </Button>
      </Box>
    </Box>
  );
}
